import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useLocation } from '../../navigation'
import { type Move } from './models/move.model'
import * as selectors from './moves.selectors'
import { estimateRequirements, estimateVolume } from '../../../mwLib'
import { type MoveContentPayload } from '../../../mwLib/mw.types'
import { keyValueMapToRecord } from '../../../common/keyValueMap'
import { type MoveLocations } from './models/subModels/moveLocations.models'
import { LocationType } from './moves.enums'
import { getLocationById } from './locations.utils'
import { useTranslation } from 'react-i18next'
import { capitalizeFirstLetter } from '../../../utils/string'

export enum ACTION_TYPES {
  SET_MOVE_ID = 'SET_MOVE_ID',
  SET_IS_LOGGING_OUT = 'SET_IS_LOGGING_OUT',
}

export const setMoveId = (moveId: string | undefined) => ({
  type: ACTION_TYPES.SET_MOVE_ID as const,
  moveId,
})

export const setIsLoggingOut = () => ({
  type: ACTION_TYPES.SET_IS_LOGGING_OUT as const,
})

export type Actions = ReturnType<typeof setMoveId> | ReturnType<typeof setIsLoggingOut>

/**
 * remove the currently saved move
 */
export const useLogoutAction = () => {
  const dispatch = useDispatch()
  const removeSavedId = useRemoveSavedMoveIdAction()
  return () => {
    dispatch(setIsLoggingOut())
    removeSavedId()
    setTimeout(() => {
      document.location = '/'
    }, 100)
  }
}

/**
 * returns current move public id based on routing
 * this is not using useParams on purpose since we want to read the move id outside of the route
 */
export const useMovePublicId = () => {
  const location = useLocation()
  return useMemo(() => {
    return location.pathname.match(/\/move\/([^/]+)/i)?.[1]
  }, [location.pathname])
}

/**
 * returned the last move of the user (saved in store)
 */
export const useSavedMoveId = () => {
  return useSelector(selectors.moveId)
}

/**
 * save the move id in store
 */
export const useSaveMoveAction = () => {
  const savedMoveId = useSavedMoveId()
  const isLoggingOut = useSelector(selectors.isLoggingOut)
  const dispatch = useDispatch()

  return (move: Move) => {
    if (isLoggingOut || move.publicId === savedMoveId) {
      return
    }
    dispatch(setMoveId(move.publicId))
    localStorage.removeItem('quotesView')
  }
}

/**
 * remove the currently saved move
 */
export const useRemoveSavedMoveIdAction = () => {
  const dispatch = useDispatch()
  return () => {
    dispatch(setMoveId(undefined))
  }
}

export const useMoveEstimatedRequirements = () => {
  return (move: Move) => {
    const content: MoveContentPayload = {
      residenceType: move.request.residence.residenceType,
      residenceRooms: keyValueMapToRecord(move.request.residence.residenceRooms),
      nbBoxes: move.request.content.nbBoxes,
      furnitureRatio: move.request.content.furnitureRatio,
      specialItems: keyValueMapToRecord(move.request.content.specialItems),
    }
    return estimateRequirements(estimateVolume(content))
  }
}

export const getLocationType = (locations: MoveLocations, locationId: string) => {
  if (locations.origin?.id === locationId) {
    return LocationType.Origin
  }
  if (locations.destination?.id === locationId) {
    return LocationType.Destination
  }
  if (locations.warehouse?.id === locationId) {
    return LocationType.Warehouse
  }
  if (locations.stops?.some((stop) => stop.id === locationId)) {
    return LocationType.Stop
  }
}

export const getLocationFieldName = (locations: MoveLocations, locationId: string) => {
  switch (getLocationType(locations, locationId)) {
    case LocationType.Origin:
      return 'origin'
    case LocationType.Destination:
      return 'destination'
    case LocationType.Warehouse:
      return 'warehouse'
    case LocationType.Stop:
    {
      const index = locations.stops?.findIndex((stop) => stop.id === locationId)
      return index === -1 ? '' : `stops.${String(index)}`
    }
    default:
      return ''
  }
}

type FormatTypeOptions = {
  description?: string
  duration?: string
}

const useFormatType = () => {
  const { t } = useTranslation()

  return (type: LocationType, {
    description,
    duration,
  }: FormatTypeOptions = {}) => {
    switch (type) {
      case LocationType.Origin:
      case LocationType.Destination:
      case LocationType.Warehouse:
        return t(`components.move.addresses.locationType.${type}`)
      case LocationType.Stop:
      {
        const parts = [
          duration ? `${t(`components.move.addresses.locationType.${type}`)} (${duration}${t('units.hour')})` : t(`components.move.addresses.locationType.${type}`),
          description ? `- ${capitalizeFirstLetter(description) ?? ''}` : '',
        ]
        return parts.filter(p => !!p).join(' ')
      }
    }
  }
}

export const useGetLocationLabel = () => {
  const formatType = useFormatType()

  return (moveLocations: MoveLocations, locationId: string) => {
    const location = getLocationById(moveLocations, locationId)
    const type = getLocationType(moveLocations, locationId)
    if (!location || !type) {
      return
    }
    return formatType(type, {
      description: 'description' in location ? String(location.description) : undefined,
      duration: 'stopDurationHours' in location ? String(location.stopDurationHours) : undefined,
    })
  }
}
